import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import { uploadImageToCloud } from "@/utils/cloudImageUpload";
import { defaultHero, type HeroData } from "@/data/siteContent";

export const HERO_DATA_KEY = "ma_hero";

interface HeroSectionProps {
  dataKey?: string;
}

export default function HeroSection({ dataKey }: HeroSectionProps = {}) {
  const storageKey = dataKey || HERO_DATA_KEY;
  const { isEditing } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const navigate = useNavigate();
  const [data, setData] = useAdminStorage<HeroData>(storageKey, defaultHero);

  const [heroUploadError, setHeroUploadError] = useState<string | null>(null);

  const handleBgReplace = useCallback(async (url: string) => {
    setHeroUploadError(null);
    // If it's a base64 data URL, upload to cloud first so it's accessible everywhere
    if (url.startsWith("data:")) {
      try {
        const cloudUrl = await uploadImageToCloud(url);
        setData({ ...data, bgImage: cloudUrl });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setHeroUploadError(msg);
        console.error("Hero upload error:", msg);
        // Still show the image locally even if cloud upload failed
        setData({ ...data, bgImage: url });
      }
    } else {
      setData({ ...data, bgImage: url });
    }
  }, [data, setData]);

  const set = useCallback(<K extends keyof HeroData>(key: K, val: HeroData[K]) => {
    setData({ ...data, [key]: val });
  }, [data, setData]);

  const setStat = useCallback((i: number, field: "value" | "label", val: string) => {
    const stats = data.stats.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    setData({ ...data, stats });
  }, [data, setData]);

  const addStat = useCallback(() => {
    const stats = [...data.stats, { value: "100%", label: "New Metric" }];
    setData({ ...data, stats });
  }, [data, setData]);

  const deleteStat = useCallback((i: number) => {
    const stats = data.stats.filter((_, idx) => idx !== i);
    setData({ ...data, stats });
  }, [data, setData]);

  const handleCtaClick = useCallback((url: string) => {
    if (url.startsWith("http")) { window.open(url, "_blank", "noopener noreferrer"); return; }
    const el = document.querySelector(url);
    if (el) { el.scrollIntoView({ behavior: "smooth" }); } else { navigate(url); }
  }, [navigate]);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full">
        {isEditing ? (
          <AdminImageUpload
            src={data.bgImage}
            alt="Hero background"
            onReplace={handleBgReplace}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover object-top"
            objectPosition={(data as HeroData & { bgPosition?: string }).bgPosition ?? "50% 20%"}
            onPositionChange={(pos) => setData({ ...data, bgPosition: pos } as HeroData & { bgPosition: string })}
            mobileObjectPosition={(data as HeroData & { bgMobilePosition?: string }).bgMobilePosition ?? "50% 20%"}
            onMobilePositionChange={(pos) => setData({ ...data, bgMobilePosition: pos } as HeroData & { bgMobilePosition: string })}
          />
        ) : (
          data.bgImage ? (
            <img src={data.bgImage} alt="Retail shelves full of handmade art" className="w-full h-full object-cover object-top" />
          ) : null
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-pink-900/60 via-pink-800/50 to-pink-900/70"></div>
      </div>

      {/* Hero upload error banner */}
      {isEditing && heroUploadError && (
        <div className="absolute top-20 left-0 right-0 z-20 flex justify-center px-4">
          <div className="bg-red-500/90 text-white text-xs font-bold px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
            <i className="ri-error-warning-line"></i>
            Hero upload failed: {heroUploadError}
            <button onClick={() => setHeroUploadError(null)} className="ml-2 text-white/80 hover:text-white cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>
      )}

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill={isDark ? "#030712" : "#fff9fb"} />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full text-center px-6 py-32 flex flex-col items-center">
        {/* Badge — always visible in admin mode */}
        {(isEditing || data.badge) && (
          isEditing ? (
            <AdminEditField
              value={data.badge}
              onSave={(v) => set("badge", v)}
              onDelete={() => set("badge", "")}
              className="mb-6"
              inputClassName="text-center"
              label="Badge text"
            >
              <span className="inline-block bg-green-400/90 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
                {data.badge}
              </span>
            </AdminEditField>
          ) : (
            <span className="inline-block bg-green-400/90 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
              {data.badge}
            </span>
          )
        )}

        {/* Headline — always visible in admin mode */}
        {(isEditing || data.headline1 || data.headlineAccent) && (
          <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight mb-4 max-w-3xl" style={{ fontFamily: headingFont, ...headingStyle }}>
            {isEditing ? (
              <span className="block">
                <span className="block">
                  <AdminEditField
                    value={data.headline1}
                    onSave={(v) => set("headline1", v)}
                    onDelete={() => set("headline1", "")}
                    inputClassName="text-center text-3xl md:text-5xl lg:text-7xl font-extrabold"
                  >
                    <span>{data.headline1}</span>
                  </AdminEditField>
                </span>
                <span className="block text-green-300">
                  <AdminEditField
                    value={data.headlineAccent}
                    onSave={(v) => set("headlineAccent", v)}
                    onDelete={() => set("headlineAccent", "")}
                    inputClassName="text-center text-3xl md:text-5xl lg:text-7xl font-extrabold"
                  >
                    <span>{data.headlineAccent}</span>
                  </AdminEditField>
                </span>
              </span>
            ) : (
              <>{data.headline1}{data.headline1 && data.headlineAccent ? <br /> : null}<span className="text-green-300">{data.headlineAccent}</span></>
            )}
          </h1>
        )}

        {/* Tagline — always visible in admin mode */}
        {(isEditing || data.tagline) && (
          isEditing ? (
            <AdminEditField
              value={data.tagline}
              onSave={(v) => set("tagline", v)}
              onDelete={() => set("tagline", "")}
              className="mb-3 max-w-2xl"
              inputClassName="text-center text-xl font-medium"
            >
              <p className="text-xl md:text-2xl text-pink-100 font-medium">{data.tagline}</p>
            </AdminEditField>
          ) : (
            <p className="text-xl md:text-2xl text-pink-100 font-medium mb-3 max-w-2xl">{data.tagline}</p>
          )
        )}

        {/* Body — always visible in admin mode */}
        {(isEditing || data.body) && (
          isEditing ? (
            <AdminEditField
              value={data.body}
              onSave={(v) => set("body", v)}
              onDelete={() => set("body", "")}
              multiline
              richText
              className="mb-10 max-w-xl"
              inputClassName="text-center"
            >
              <RichText html={data.body} as="p" className="text-base md:text-lg text-white/80 leading-relaxed" />
            </AdminEditField>
          ) : (
            <RichText html={data.body} as="p" className="text-base md:text-lg text-white/80 max-w-xl mb-10 leading-relaxed" />
          )
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          {isEditing ? (
            <>
              <div className="flex flex-col items-center gap-1.5">
                <AdminEditField value={data.ctaPrimary} onSave={(v) => set("ctaPrimary", v)} inputClassName="text-center font-bold">
                  <button className="bg-pink-500 hover:bg-pink-600 text-white font-bold text-base px-8 py-4 rounded-full flex items-center gap-3 transition-all whitespace-nowrap cursor-pointer">
                    <span className="w-8 h-8 flex items-center justify-center bg-green-400 rounded-full">
                      <i className="ri-brush-line text-white text-sm"></i>
                    </span>
                    {data.ctaPrimary}
                    <i className="ri-arrow-right-up-line text-lg"></i>
                  </button>
                </AdminEditField>
                <AdminEditField value={data.ctaPrimaryUrl} onSave={(v) => set("ctaPrimaryUrl", v)} inputClassName="text-xs text-center text-white/60">
                  <span className="text-xs text-white/60">URL: {data.ctaPrimaryUrl || "#application"}</span>
                </AdminEditField>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <AdminEditField value={data.ctaSecondary} onSave={(v) => set("ctaSecondary", v)} inputClassName="text-center font-semibold">
                  <button className="border-2 border-white text-white font-semibold text-base px-8 py-4 rounded-full whitespace-nowrap cursor-pointer">
                    {data.ctaSecondary}
                  </button>
                </AdminEditField>
                <AdminEditField value={data.ctaSecondaryUrl} onSave={(v) => set("ctaSecondaryUrl", v)} inputClassName="text-xs text-center text-white/60">
                  <span className="text-xs text-white/60">URL: {data.ctaSecondaryUrl || "#spaces"}</span>
                </AdminEditField>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => handleCtaClick(data.ctaPrimaryUrl || "#application")} className="bg-pink-500 hover:bg-pink-600 text-white font-bold text-base px-8 py-4 rounded-full flex items-center gap-3 transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
                <span className="w-8 h-8 flex items-center justify-center bg-green-400 rounded-full">
                  <i className="ri-brush-line text-white text-sm"></i>
                </span>
                {data.ctaPrimary}
                <i className="ri-arrow-right-up-line text-lg"></i>
              </button>
              <button onClick={() => handleCtaClick(data.ctaSecondaryUrl || "#spaces")} className="border-2 border-white text-white font-semibold text-base px-8 py-4 rounded-full hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                {data.ctaSecondary}
              </button>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="mt-16 flex flex-col sm:flex-row gap-8 sm:gap-16 items-center">
          {data.stats.map((stat, i) => (
            <div key={i} className={`text-center relative ${isEditing ? "group/stat" : ""}`}>
              {isEditing && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteStat(i); }}
                  className="absolute -top-3 -right-3 z-20 w-5 h-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/stat:opacity-100"
                  title="Remove metric"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              )}
              {isEditing ? (
                <div className="flex flex-col items-center gap-1">
                  <AdminEditField
                    value={stat.value}
                    onSave={(v) => setStat(i, "value", v)}
                    inputClassName="text-center font-extrabold text-2xl"
                    className="text-3xl font-extrabold text-green-300"
                  >
                    <div className="text-3xl font-extrabold text-green-300" style={{ fontFamily: headingFont, ...headingStyle }}>{stat.value}</div>
                  </AdminEditField>
                  <AdminEditField
                    value={stat.label}
                    onSave={(v) => setStat(i, "label", v)}
                    inputClassName="text-center text-xs uppercase tracking-widest"
                    className="text-white/70 text-sm uppercase tracking-widest"
                  >
                    <div className="text-white/70 text-sm uppercase tracking-widest">{stat.label}</div>
                  </AdminEditField>
                </div>
              ) : (
                <>
                  <div className="text-3xl font-extrabold text-green-300" style={{ fontFamily: headingFont, ...headingStyle }}>{stat.value}</div>
                  <div className="text-white/70 text-sm uppercase tracking-widest mt-1">{stat.label}</div>
                </>
              )}
            </div>
          ))}
          {isEditing && (
            <button
              onClick={addStat}
              className="flex flex-col items-center gap-1 text-white/40 hover:text-white/70 transition-colors cursor-pointer px-3 py-2"
              title="Add new metric"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-dashed border-white/30 hover:border-white/50">
                <i className="ri-add-line text-sm"></i>
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold">Add metric</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}